import { useDeferredValue, useEffect, useMemo, useState, startTransition } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { logoutAdmin } from '../lib/admin-auth-api';
import {
  disableAdminAccountMembership,
  restoreAdminAccountMembership,
  searchAdminAccountConsole,
  type AdminAccountConsoleItem,
  type AdminAccountConsoleListResponse,
} from '../lib/admin-account-console-api';
import { formatSubscriptionPlanLabel } from '../lib/subscription-plan-label';
import { YOUTU_BRAND_NAME } from '../lib/youtu-brand';
import {
  createAdminWechatPermanentEntry,
  disableAdminWechatPermanentEntry,
  getAdminWechatPermanentEntry,
  getAdminWechatPermanentEntryActivity,
  listAdminWechatPermanentEntries,
  type AdminWechatPermanentEntryActivity,
  type AdminWechatPermanentEntryDetail,
  type AdminWechatPermanentEntryListItem,
  type AdminWechatPermanentEntryListResponse,
} from '../lib/admin-wechat-permanent-entry-api';
import { useAdminAuthStore } from '../stores/useAdminAuthStore';
import AdminCoreMemoryPanel from '../components/admin/AdminCoreMemoryPanel';

type EntryStatusFilter = 'all' | 'active' | 'disabled' | 'expired';

type CreateFormState = {
  avatarKey: string;
  displayName: string;
  profile: string;
  agentIds: string;
  defaultAgentId: string;
  expiresAt: string;
};

const EMPTY_CREATE_FORM: CreateFormState = {
  avatarKey: '',
  displayName: '',
  profile: '',
  agentIds: '',
  defaultAgentId: '',
  expiresAt: '',
};

const STATUS_FILTERS: Array<{ value: EntryStatusFilter; label: string }> = [
  { value: 'all', label: 'All States' },
  { value: 'active', label: 'Active' },
  { value: 'disabled', label: 'Disabled' },
  { value: 'expired', label: 'Expired' },
];

function parseAgentIds(value: string): string[] {
  return Array.from(
    new Set(
      value
        .split(/[\n,]/g)
        .map((item) => item.trim())
        .filter(Boolean),
    ),
  );
}

function toIsoOrNull(value: string): string | null {
  if (!value.trim()) {
    return null;
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.valueOf())) {
    return null;
  }
  return parsed.toISOString();
}

function formatTimestamp(value: string | null): string {
  if (!value) {
    return 'N/A';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.valueOf())) {
    return value;
  }
  return new Intl.DateTimeFormat('en', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(parsed);
}

function formatPercentage(value: number): string {
  return `${Math.round(value * 100)}%`;
}

function formatAmountMinor(value: number, currency: string): string {
  return `${currency} ${(value / 100).toFixed(2)}`;
}

function statusBadgeClasses(status: string): string {
  switch (status) {
    case 'active':
      return 'border-emerald-400/30 bg-emerald-400/12 text-emerald-100';
    case 'disabled':
      return 'border-rose-400/30 bg-rose-400/12 text-rose-100';
    case 'expired':
      return 'border-amber-300/30 bg-amber-300/12 text-amber-100';
    case 'confirmed':
      return 'border-cyan-300/30 bg-cyan-300/12 text-cyan-100';
    case 'pending':
      return 'border-sky-300/30 bg-sky-300/12 text-sky-100';
    case 'revoked':
      return 'border-fuchsia-300/30 bg-fuchsia-300/12 text-fuchsia-100';
    default:
      return 'border-white/15 bg-white/10 text-slate-200';
  }
}

function surfaceCardClasses(isSelected = false): string {
  return [
    'relative overflow-hidden rounded-[28px] border p-5 transition duration-300',
    isSelected
      ? 'border-amber-300/45 bg-[linear-gradient(180deg,rgba(251,191,36,0.16),rgba(15,23,42,0.92))] shadow-[0_18px_60px_rgba(245,158,11,0.18)]'
      : 'border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.05),rgba(15,23,42,0.92))] hover:border-amber-200/25 hover:bg-[linear-gradient(180deg,rgba(255,255,255,0.07),rgba(15,23,42,0.94))]',
  ].join(' ');
}

async function copyText(value: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }

  const input = document.createElement('textarea');
  input.value = value;
  input.style.position = 'fixed';
  input.style.opacity = '0';
  document.body.appendChild(input);
  input.select();
  document.execCommand('copy');
  document.body.removeChild(input);
}

export default function AdminConsolePage() {
  const navigate = useNavigate();
  const accessToken = useAdminAuthStore((state) => state.accessToken);
  const user = useAdminAuthStore((state) => state.user);
  const clearSession = useAdminAuthStore((state) => state.clearSession);
  const setSuppressBootstrap = useAdminAuthStore((state) => state.setSuppressBootstrap);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [entriesResponse, setEntriesResponse] = useState<AdminWechatPermanentEntryListResponse | null>(null);
  const [selectedEntryId, setSelectedEntryId] = useState<string | null>(null);
  const [selectedEntry, setSelectedEntry] = useState<AdminWechatPermanentEntryDetail | null>(null);
  const [activity, setActivity] = useState<AdminWechatPermanentEntryActivity | null>(null);
  const [isLoadingEntries, setIsLoadingEntries] = useState(true);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [entriesError, setEntriesError] = useState<string | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const deferredQuery = useDeferredValue(query);
  const [statusFilter, setStatusFilter] = useState<EntryStatusFilter>('all');
  const [reloadNonce, setReloadNonce] = useState(0);
  const [createForm, setCreateForm] = useState<CreateFormState>(EMPTY_CREATE_FORM);
  const [isCreating, setIsCreating] = useState(false);
  const [isDisabling, setIsDisabling] = useState(false);
  const [disableArmed, setDisableArmed] = useState(false);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [copiedUrlEntryId, setCopiedUrlEntryId] = useState<string | null>(null);
  const [accountQuery, setAccountQuery] = useState('');
  const [accountResults, setAccountResults] = useState<AdminAccountConsoleListResponse | null>(null);
  const [selectedAccountConsumerId, setSelectedAccountConsumerId] = useState<string | null>(null);
  const [isSearchingAccounts, setIsSearchingAccounts] = useState(false);
  const [isUpdatingMembership, setIsUpdatingMembership] = useState(false);
  const [accountError, setAccountError] = useState<string | null>(null);
  const [accountActionMessage, setAccountActionMessage] = useState<string | null>(null);
  const isAgentOperator = user?.role === 'agent_operator';
  const scopedAgentId = user?.scoped_agent_id ?? null;

  const selectedListItem = useMemo<AdminWechatPermanentEntryListItem | null>(() => {
    return entriesResponse?.items.find((item) => item.id === selectedEntryId) ?? null;
  }, [entriesResponse, selectedEntryId]);

  const selectedAccount = useMemo<AdminAccountConsoleItem | null>(() => {
    return (
      accountResults?.items.find(
        (item) => item.consumerUser.id === selectedAccountConsumerId,
      ) ?? accountResults?.items[0] ?? null
    );
  }, [accountResults, selectedAccountConsumerId]);

  useEffect(() => {
    if (!accessToken || isAgentOperator) {
      setIsLoadingEntries(false);
      setEntriesResponse(null);
      setSelectedEntryId(null);
      return;
    }

    let ignore = false;
    setIsLoadingEntries(true);
    setEntriesError(null);

    void listAdminWechatPermanentEntries(accessToken, {
      q: deferredQuery,
      status: statusFilter,
      limit: 60,
    })
      .then((response) => {
        if (ignore) {
          return;
        }
        setEntriesResponse(response);
        setSelectedEntryId((current) => {
          if (current && response.items.some((item) => item.id === current)) {
            return current;
          }
          return response.items[0]?.id ?? null;
        });
      })
      .catch((error) => {
        if (ignore) {
          return;
        }
        setEntriesError(error instanceof Error ? error.message : 'Failed to load permanent entries.');
        setEntriesResponse(null);
        setSelectedEntryId(null);
      })
      .finally(() => {
        if (!ignore) {
          setIsLoadingEntries(false);
        }
      });

    return () => {
      ignore = true;
    };
  }, [accessToken, deferredQuery, isAgentOperator, statusFilter, reloadNonce]);

  useEffect(() => {
    if (!accessToken || !selectedEntryId || isAgentOperator) {
      setSelectedEntry(null);
      setActivity(null);
      setIsLoadingDetail(false);
      return;
    }

    let ignore = false;
    setIsLoadingDetail(true);
    setDetailError(null);

    void Promise.all([
      getAdminWechatPermanentEntry(accessToken, selectedEntryId),
      getAdminWechatPermanentEntryActivity(accessToken, selectedEntryId, 8),
    ])
      .then(([detail, nextActivity]) => {
        if (ignore) {
          return;
        }
        setSelectedEntry(detail);
        setActivity(nextActivity);
      })
      .catch((error) => {
        if (ignore) {
          return;
        }
        setSelectedEntry(null);
        setActivity(null);
        setDetailError(
          error instanceof Error ? error.message : 'Failed to load entry detail.',
        );
      })
      .finally(() => {
        if (!ignore) {
          setIsLoadingDetail(false);
          setDisableArmed(false);
        }
      });

    return () => {
      ignore = true;
    };
  }, [accessToken, isAgentOperator, selectedEntryId, reloadNonce]);

  const handleLogout = async () => {
    setIsLoggingOut(true);
    setSuppressBootstrap(true);
    let logoutWarning: string | null = null;

    try {
      await logoutAdmin();
    } catch (err) {
      logoutWarning = 'Server logout failed; local session cleared.';
    } finally {
      clearSession();
      setIsLoggingOut(false);
      navigate('/admin/login', {
        replace: true,
        state: logoutWarning ? { logoutWarning } : null,
      });
    }
  };

  const handleCreateEntry = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!accessToken) {
      return;
    }

    const agentIds = parseAgentIds(createForm.agentIds);
    const expiresAt = toIsoOrNull(createForm.expiresAt);
    if (!createForm.avatarKey.trim() || !createForm.displayName.trim() || !createForm.profile.trim()) {
      setActionMessage('Avatar key, display name, and profile are required.');
      return;
    }
    if (agentIds.length === 0) {
      setActionMessage('At least one agent id is required.');
      return;
    }
    if (!createForm.defaultAgentId.trim()) {
      setActionMessage('Default agent id is required.');
      return;
    }

    setIsCreating(true);
    setActionMessage(null);

    try {
      const created = await createAdminWechatPermanentEntry(accessToken, {
        avatarKey: createForm.avatarKey.trim(),
        displayName: createForm.displayName.trim(),
        profile: createForm.profile.trim(),
        agentIds,
        defaultAgentId: createForm.defaultAgentId.trim(),
        expiresAt,
      });

      setCreateForm(EMPTY_CREATE_FORM);
      setSelectedEntryId(created.id);
      setActionMessage('Permanent entry created and ready for issuance.');
      startTransition(() => {
        setReloadNonce((current) => current + 1);
      });
    } catch (error) {
      setActionMessage(
        error instanceof Error ? error.message : 'Failed to create permanent entry.',
      );
    } finally {
      setIsCreating(false);
    }
  };

  const handleDisableEntry = async () => {
    if (!accessToken || !selectedEntryId) {
      return;
    }

    setIsDisabling(true);
    setActionMessage(null);

    try {
      await disableAdminWechatPermanentEntry(accessToken, selectedEntryId);
      setActionMessage('Permanent entry disabled.');
      startTransition(() => {
        setReloadNonce((current) => current + 1);
      });
    } catch (error) {
      setActionMessage(
        error instanceof Error ? error.message : 'Failed to disable permanent entry.',
      );
    } finally {
      setDisableArmed(false);
      setIsDisabling(false);
    }
  };

  const handleAccountSearch = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!accessToken) {
      return;
    }
    const cleanedQuery = accountQuery.trim();
    if (!cleanedQuery) {
      setAccountError('Enter a phone, phone suffix, or email.');
      return;
    }

    setIsSearchingAccounts(true);
    setAccountError(null);
    setAccountActionMessage(null);

    try {
      const result = await searchAdminAccountConsole(accessToken, {
        q: cleanedQuery,
        limit: 20,
      });
      setAccountResults(result);
      setSelectedAccountConsumerId(result.items[0]?.consumerUser.id ?? null);
      if (result.items.length === 0) {
        setAccountActionMessage('No matching account found.');
      }
    } catch (error) {
      setAccountResults(null);
      setSelectedAccountConsumerId(null);
      setAccountError(error instanceof Error ? error.message : 'Account search failed.');
    } finally {
      setIsSearchingAccounts(false);
    }
  };

  const replaceAccountResult = (item: AdminAccountConsoleItem) => {
    setAccountResults((current) => {
      if (!current) {
        return { items: [item], total: 1 };
      }
      const exists = current.items.some(
        (existing) => existing.consumerUser.id === item.consumerUser.id,
      );
      return {
        ...current,
        items: exists
          ? current.items.map((existing) =>
              existing.consumerUser.id === item.consumerUser.id ? item : existing,
            )
          : [item, ...current.items],
      };
    });
    setSelectedAccountConsumerId(item.consumerUser.id);
  };

  const handleMembershipAction = async (
    accountId: string,
    action: 'disable' | 'restore',
  ) => {
    if (!accessToken) {
      return;
    }

    setIsUpdatingMembership(true);
    setAccountError(null);
    setAccountActionMessage(null);

    try {
      const nextItem =
        action === 'disable'
          ? await disableAdminAccountMembership(accessToken, accountId)
          : await restoreAdminAccountMembership(accessToken, accountId);
      replaceAccountResult(nextItem);
      setAccountActionMessage(
        action === 'disable' ? 'Account login disabled.' : 'Account login restored.',
      );
    } catch (error) {
      setAccountError(
        error instanceof Error ? error.message : 'Account login update failed.',
      );
    } finally {
      setIsUpdatingMembership(false);
    }
  };

  const handleCopyPublicUrl = async (
    entry: AdminWechatPermanentEntryListItem | AdminWechatPermanentEntryDetail,
  ) => {
    try {
      await copyText(entry.publicUrl);
      setCopiedUrlEntryId(entry.id);
      window.setTimeout(() => {
        setCopiedUrlEntryId((current) => (current === entry.id ? null : current));
      }, 1600);
    } catch (error) {
      setActionMessage(error instanceof Error ? error.message : 'Failed to copy public URL.');
    }
  };

  const summary = entriesResponse?.summary;

  return (
    <div
      className="relative min-h-screen overflow-hidden bg-[#08111b] px-4 py-6 text-stone-100 sm:px-6 lg:px-8"
      style={{ fontFamily: '"IBM Plex Sans", "Segoe UI", sans-serif' }}
    >
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_16%_18%,rgba(251,191,36,0.14),transparent_28%),radial-gradient(circle_at_78%_22%,rgba(56,189,248,0.12),transparent_26%),radial-gradient(circle_at_64%_68%,rgba(245,158,11,0.08),transparent_34%),linear-gradient(180deg,rgba(15,23,42,0.65),rgba(2,6,23,0.96))]" />
        <div
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage:
              'repeating-linear-gradient(118deg, transparent 0 24px, rgba(229, 191, 98, 0.05) 24px 25px, transparent 25px 52px)',
            maskImage:
              'radial-gradient(circle at 32% 28%, black 0, transparent 46%), radial-gradient(circle at 72% 60%, black 0, transparent 54%)',
          }}
        />
        <div className="absolute left-[-8%] top-[7%] h-72 w-72 rounded-full bg-amber-300/8 blur-3xl" />
        <div className="absolute right-[-10%] top-[24%] h-96 w-96 rounded-full bg-cyan-300/8 blur-3xl" />
      </div>

      <div className="relative mx-auto flex max-w-[1560px] flex-col gap-6">
        <section className="overflow-hidden rounded-[34px] border border-white/10 bg-[linear-gradient(145deg,rgba(255,255,255,0.08),rgba(12,19,33,0.96))] px-6 py-7 shadow-[0_30px_100px_rgba(2,6,23,0.52)] backdrop-blur xl:px-8">
          <div className="grid gap-6 xl:grid-cols-[1.08fr_0.92fr]">
            <div className="space-y-5">
              <div className="flex flex-wrap items-center gap-3">
                <span className="rounded-full border border-amber-300/30 bg-amber-300/10 px-3 py-1 text-[11px] uppercase tracking-[0.34em] text-amber-100">
                  {isAgentOperator ? 'Agent Operator' : 'Fine Strand Control'}
                </span>
                <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-slate-300">
                  Logged in as {user?.username ?? 'unknown'} | {user?.role ?? 'unknown'}
                </span>
                {isAgentOperator && scopedAgentId ? (
                  <span className="rounded-full border border-emerald-300/25 bg-emerald-300/10 px-3 py-1 text-xs text-emerald-100">
                    Agent {scopedAgentId}
                  </span>
                ) : null}
              </div>

              <div className="max-w-3xl space-y-3">
                <h1
                  className="text-4xl font-semibold tracking-[-0.04em] text-white sm:text-5xl"
                  style={{ fontFamily: '"Iowan Old Style", "Palatino Linotype", "Book Antiqua", serif' }}
                >
                  {isAgentOperator ? 'Agent Account Console' : 'Avatar Entry Operations'}
                </h1>
                <p className="max-w-2xl text-sm leading-7 text-slate-300 sm:text-base">
                  {isAgentOperator
                    ? 'Search account records, review agent usage, and manage membership for the assigned agent.'
                    : 'A modern operating deck for permanent entry orchestration. Long-lived tokens, short-lived bridges, and reconnection trails all surface here without breaking the calm.'}
                </p>
              </div>

              <div className="grid gap-3 sm:grid-cols-4">
                <div className="rounded-[24px] border border-white/10 bg-slate-950/45 p-4">
                  <p className="text-[11px] uppercase tracking-[0.28em] text-slate-400">Active Entries</p>
                  <p className="mt-3 text-3xl font-semibold text-white">{summary?.activeEntries ?? '0'}</p>
                </div>
                <div className="rounded-[24px] border border-white/10 bg-slate-950/45 p-4">
                  <p className="text-[11px] uppercase tracking-[0.28em] text-slate-400">Live Bridges</p>
                  <p className="mt-3 text-3xl font-semibold text-white">{summary?.pendingIssues ?? '0'}</p>
                </div>
                <div className="rounded-[24px] border border-white/10 bg-slate-950/45 p-4">
                  <p className="text-[11px] uppercase tracking-[0.28em] text-slate-400">Success Rate</p>
                  <p className="mt-3 text-3xl font-semibold text-white">
                    {summary ? formatPercentage(summary.confirmationRate24h) : '0%'}
                  </p>
                </div>
                <div className="rounded-[24px] border border-white/10 bg-slate-950/45 p-4">
                  <p className="text-[11px] uppercase tracking-[0.28em] text-slate-400">24h Confirmed</p>
                  <p className="mt-3 text-3xl font-semibold text-white">{summary?.confirmedIssues24h ?? '0'}</p>
                </div>
              </div>
            </div>

            <div className="flex flex-col justify-between gap-4 rounded-[28px] border border-white/10 bg-[linear-gradient(180deg,rgba(10,18,31,0.74),rgba(2,6,23,0.92))] p-5">
              <div className="space-y-4">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.28em] text-slate-400">
                      {isAgentOperator ? 'Agent Scope' : 'Current Morph'}
                    </p>
                    <p className="mt-2 text-xl font-semibold text-white">
                      {isAgentOperator
                        ? scopedAgentId ?? 'No agent scope'
                        : selectedEntry?.displayName ?? selectedListItem?.displayName ?? 'No entry selected'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {!isAgentOperator ? (
                      <>
                        <Link
                          className="rounded-full border border-sky-200/35 bg-sky-200/10 px-4 py-2 text-sm text-sky-100 transition hover:border-sky-200/55 hover:bg-sky-200/20"
                          to="/admin/avatar-factory"
                        >
                          Open Avatar Factory
                        </Link>
                        <Link
                          className="rounded-full border border-amber-200/35 bg-amber-200/10 px-4 py-2 text-sm text-amber-100 transition hover:border-amber-200/55 hover:bg-amber-200/20"
                          to="/admin/luyou-workbook-template"
                        >
                          {YOUTU_BRAND_NAME}表格模板
                        </Link>
                        <button
                          className="rounded-full border border-emerald-200/35 bg-emerald-200/10 px-4 py-2 text-sm text-emerald-100 transition hover:border-emerald-200/55 hover:bg-emerald-200/20"
                          onClick={() => navigate('/admin/avoman-calibration')}
                          type="button"
                        >
                          Avoman Review
                        </button>
                      </>
                    ) : null}
                    <button
                      className="rounded-full border border-white/15 px-4 py-2 text-sm text-slate-200 transition hover:border-amber-200/30 hover:bg-white/5 disabled:cursor-not-allowed disabled:opacity-60"
                      disabled={isLoggingOut}
                      onClick={handleLogout}
                      type="button"
                    >
                      {isLoggingOut ? 'Signing out...' : 'Logout'}
                    </button>
                  </div>
                </div>

                <div className="grid gap-3 sm:grid-cols-3">
                  <div className="rounded-[22px] border border-white/10 bg-white/5 p-4">
                    <p className="text-[11px] uppercase tracking-[0.24em] text-slate-400">Total</p>
                    <p className="mt-2 text-lg font-semibold text-white">{summary?.totalEntries ?? 0}</p>
                  </div>
                  <div className="rounded-[22px] border border-white/10 bg-white/5 p-4">
                    <p className="text-[11px] uppercase tracking-[0.24em] text-slate-400">Disabled</p>
                    <p className="mt-2 text-lg font-semibold text-white">{summary?.disabledEntries ?? 0}</p>
                  </div>
                  <div className="rounded-[22px] border border-white/10 bg-white/5 p-4">
                    <p className="text-[11px] uppercase tracking-[0.24em] text-slate-400">Expired</p>
                    <p className="mt-2 text-lg font-semibold text-white">{summary?.expiredEntries ?? 0}</p>
                  </div>
                </div>
              </div>

              {actionMessage ? (
                <div className="rounded-[22px] border border-amber-300/20 bg-amber-300/10 px-4 py-3 text-sm text-amber-50">
                  {actionMessage}
                </div>
              ) : (
                <p className="text-sm leading-6 text-slate-400">
                  Public URLs stay stable. Every reconnect still burns a fresh iLink bridge.
                </p>
              )}
            </div>
          </div>
        </section>

        <section className="overflow-hidden rounded-[32px] border border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.055),rgba(12,19,33,0.94))] p-5 shadow-[0_20px_80px_rgba(2,6,23,0.42)]">
          <div className="flex flex-col gap-4 border-b border-white/10 pb-5 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="text-[11px] uppercase tracking-[0.3em] text-slate-400">Membership Desk</p>
              <h2
                className="mt-2 text-2xl font-semibold text-white"
                style={{ fontFamily: '"Iowan Old Style", "Palatino Linotype", "Book Antiqua", serif' }}
              >
                Houmao Account Console
              </h2>
            </div>
            <form className="flex w-full flex-col gap-3 sm:flex-row lg:max-w-2xl" onSubmit={handleAccountSearch}>
              <label className="sr-only" htmlFor="account-lookup">
                Account lookup
              </label>
              <input
                className="min-h-11 flex-1 rounded-[18px] border border-white/10 bg-slate-950/60 px-4 py-3 text-sm text-white outline-none transition placeholder:text-slate-500 focus:border-emerald-200/35"
                id="account-lookup"
                onChange={(event) => setAccountQuery(event.target.value)}
                placeholder="phone, phone suffix, or email"
                value={accountQuery}
              />
              <button
                className="min-h-11 rounded-full bg-emerald-300 px-5 py-3 text-sm font-semibold text-slate-950 transition hover:bg-emerald-200 disabled:cursor-not-allowed disabled:opacity-60"
                disabled={isSearchingAccounts}
                type="submit"
              >
                {isSearchingAccounts ? 'Searching...' : 'Search Accounts'}
              </button>
            </form>
          </div>

          {accountError ? (
            <div className="mt-5 rounded-[20px] border border-rose-400/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-100">
              {accountError}
            </div>
          ) : null}

          {accountActionMessage ? (
            <div className="mt-5 rounded-[20px] border border-emerald-300/25 bg-emerald-300/10 px-4 py-3 text-sm text-emerald-50">
              {accountActionMessage}
            </div>
          ) : null}

          <div className="mt-5 grid gap-5 xl:grid-cols-[0.42fr_0.58fr]">
            <div className="space-y-3">
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-medium text-slate-200">Matches</p>
                <span className="text-xs uppercase tracking-[0.2em] text-slate-500">
                  {accountResults?.total ?? 0} total
                </span>
              </div>

              {accountResults?.items.length ? (
                accountResults.items.map((item) => {
                  const account = item.subscriptionAccounts[0];
                  const isSelected = selectedAccount?.consumerUser.id === item.consumerUser.id;
                  return (
                    <button
                      className={`w-full rounded-[22px] border p-4 text-left transition ${
                        isSelected
                          ? 'border-emerald-300/45 bg-emerald-300/10'
                          : 'border-white/10 bg-slate-950/45 hover:border-emerald-200/25 hover:bg-white/5'
                      }`}
                      key={item.consumerUser.id}
                      onClick={() => setSelectedAccountConsumerId(item.consumerUser.id)}
                      type="button"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-white">
                            {account?.username ?? item.consumerUser.displayName ?? item.consumerUser.id}
                          </p>
                          <p className="mt-1 truncate text-xs text-slate-500">{item.consumerUser.id}</p>
                        </div>
                        <span
                          className={`shrink-0 rounded-full border px-2.5 py-1 text-[10px] uppercase tracking-[0.18em] ${statusBadgeClasses(item.consumerUser.status)}`}
                        >
                          {item.consumerUser.status}
                        </span>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2 text-xs text-slate-300">
                        {item.phoneMatches.map((phone) => (
                          <span className="rounded-full border border-white/10 bg-white/5 px-2.5 py-1" key={phone}>
                            {phone}
                          </span>
                        ))}
                        {item.identities
                          .filter((identity) => identity.provider === 'email')
                          .map((identity) => (
                            <span
                              className="rounded-full border border-white/10 bg-white/5 px-2.5 py-1"
                              key={identity.id}
                            >
                              {identity.identityValue}
                            </span>
                          ))}
                      </div>
                    </button>
                  );
                })
              ) : (
                <div className="rounded-[22px] border border-dashed border-white/15 bg-slate-950/45 px-4 py-8 text-sm text-slate-400">
                  Search by phone, last four digits, or email to locate an account.
                </div>
              )}
            </div>

            <div className="min-w-0 rounded-[24px] border border-white/10 bg-slate-950/45 p-4">
              {selectedAccount ? (
                <div className="space-y-5">
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="min-w-0">
                      <p className="text-[11px] uppercase tracking-[0.24em] text-slate-500">Selected Account</p>
                      <h3 className="mt-2 truncate text-xl font-semibold text-white">
                        {selectedAccount.subscriptionAccounts[0]?.username
                          ?? selectedAccount.consumerUser.displayName
                          ?? selectedAccount.consumerUser.id}
                      </h3>
                      <p className="mt-1 truncate text-xs text-slate-500">{selectedAccount.consumerUser.id}</p>
                    </div>
                    <span
                      className={`rounded-full border px-3 py-1 text-[11px] uppercase tracking-[0.22em] ${statusBadgeClasses(selectedAccount.consumerUser.status)}`}
                    >
                      {selectedAccount.consumerUser.status}
                    </span>
                  </div>

                  {accessToken && !isAgentOperator ? (
                    <AdminCoreMemoryPanel
                      accessToken={accessToken}
                      consumerUserId={selectedAccount.consumerUser.id}
                    />
                  ) : null}

                  <div className="grid gap-3 md:grid-cols-2">
                    <div className="rounded-[20px] border border-white/10 bg-white/5 p-4">
                      <p className="text-[11px] uppercase tracking-[0.24em] text-slate-500">Account login</p>
                      <p className="mt-2 text-xs leading-5 text-slate-500">
                        Controls password-account login only. Expert subscriptions are managed separately.
                      </p>
                      <div className="mt-3 space-y-3">
                        {selectedAccount.subscriptionAccounts.map((account) => (
                          <div className="rounded-[18px] border border-white/10 bg-slate-950/55 p-3" key={account.id}>
                            <div className="flex flex-wrap items-center justify-between gap-3">
                              <div className="min-w-0">
                                <p className="truncate text-sm font-semibold text-white">{account.username}</p>
                                <p className="mt-1 text-xs text-slate-500">
                                  {account.subscriptionLabel ?? 'No label'} - {account.status}
                                </p>
                              </div>
                              <button
                                className="rounded-full border border-white/10 px-3 py-2 text-xs text-slate-100 transition hover:border-emerald-200/35 hover:bg-white/5 disabled:cursor-not-allowed disabled:opacity-60"
                                disabled={isUpdatingMembership}
                                onClick={() => {
                                  void handleMembershipAction(
                                    account.id,
                                    account.status === 'active' ? 'disable' : 'restore',
                                  );
                                }}
                                type="button"
                              >
                                {account.status === 'active' ? 'Disable account' : 'Restore account'}
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="rounded-[20px] border border-white/10 bg-white/5 p-4">
                      <p className="text-[11px] uppercase tracking-[0.24em] text-slate-500">Identities</p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {selectedAccount.phoneMatches.map((phone) => (
                          <span className="rounded-full border border-white/10 bg-slate-950/60 px-3 py-1 text-xs text-slate-200" key={phone}>
                            {phone}
                          </span>
                        ))}
                        {selectedAccount.identities.map((identity) => (
                          <span className="rounded-full border border-white/10 bg-slate-950/60 px-3 py-1 text-xs text-slate-200" key={identity.id}>
                            {identity.provider}:{identity.identityType}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div>
                    <p className="text-sm font-medium text-slate-200">Payments</p>
                    <div className="mt-3 space-y-3">
                      {selectedAccount.payments.length ? (
                        selectedAccount.payments.map((payment) => (
                          <div className="rounded-[18px] border border-white/10 bg-white/5 p-4" key={payment.id}>
                            <div className="grid gap-3 md:grid-cols-4">
                              <div>
                                <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500">Amount</p>
                                <p className="mt-1 text-sm text-white">
                                  {formatAmountMinor(payment.amountMinor, payment.currency)}
                                </p>
                              </div>
                              <div>
                                <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500">Status</p>
                                <p className="mt-1 text-sm text-white">{payment.status}</p>
                              </div>
                              <div>
                                <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500">Merchant Order</p>
                                <p className="mt-1 break-all text-sm text-white">{payment.merchantOrderNo ?? 'N/A'}</p>
                              </div>
                              <div>
                                <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500">WeChat Order</p>
                                <p className="mt-1 break-all text-sm text-white">{payment.wechatTransactionId ?? 'N/A'}</p>
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="rounded-[18px] border border-dashed border-white/15 bg-slate-950/45 px-4 py-6 text-sm text-slate-400">
                          No payment records found.
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <p className="text-sm font-medium text-slate-200">Recent Chat</p>
                    <div className="mt-3 space-y-3">
                      {selectedAccount.recentMessages.length ? (
                        selectedAccount.recentMessages.map((message) => (
                          <div className="rounded-[18px] border border-white/10 bg-white/5 p-4" key={message.id}>
                            <div className="flex flex-wrap items-start justify-between gap-3">
                              <div>
                                <p className="text-sm font-semibold text-white">{message.role}</p>
                                <p className="mt-1 text-xs text-slate-500">
                                  {message.source} - {message.agentProfile ?? 'unknown agent'}
                                </p>
                              </div>
                              <span className="text-xs text-slate-500">
                                {formatTimestamp(message.createdAt)}
                              </span>
                            </div>
                            <p className="mt-3 whitespace-pre-wrap break-words text-sm leading-6 text-slate-200">
                              {message.content}
                            </p>
                          </div>
                        ))
                      ) : (
                        <div className="rounded-[18px] border border-dashed border-white/15 bg-slate-950/45 px-4 py-6 text-sm text-slate-400">
                          No recent chat records found.
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <p className="text-sm font-medium text-slate-200">Opening Records</p>
                    <div className="mt-3 grid gap-3 md:grid-cols-2">
                      {selectedAccount.accessRecords.map((record) => (
                        <div className="rounded-[18px] border border-white/10 bg-white/5 p-4" key={record.id}>
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <p className="truncate text-sm font-semibold text-white">{record.agentId}</p>
                              <p className="mt-1 text-xs text-slate-500">
                                {record.source} - {formatSubscriptionPlanLabel(record.subscriptionPlan)}
                              </p>
                            </div>
                            <span className={`rounded-full border px-2.5 py-1 text-[10px] uppercase tracking-[0.18em] ${statusBadgeClasses(record.status)}`}>
                              {record.status}
                            </span>
                          </div>
                          <p className="mt-3 text-xs text-slate-400">Expires {formatTimestamp(record.expiresAt)}</p>
                        </div>
                      ))}
                      {selectedAccount.fulfillmentRecords.map((record) => (
                        <div className="rounded-[18px] border border-white/10 bg-white/5 p-4" key={record.id}>
                          <p className="text-sm font-semibold text-white">{record.targetType}</p>
                          <p className="mt-1 break-all text-xs text-slate-500">{record.targetReference}</p>
                          <p className="mt-3 text-xs text-slate-400">
                            {record.status} - completed {formatTimestamp(record.completedAt)}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="rounded-[20px] border border-dashed border-white/15 bg-slate-950/45 px-4 py-12 text-center text-sm text-slate-400">
                  No account selected.
                </div>
              )}
            </div>
          </div>
        </section>

        {!isAgentOperator ? (
        <div className="grid gap-6 xl:grid-cols-[1.15fr_0.85fr]">
          <section className="overflow-hidden rounded-[32px] border border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.05),rgba(15,23,42,0.92))] p-5 shadow-[0_20px_80px_rgba(2,6,23,0.42)]">
            <div className="flex flex-col gap-4 border-b border-white/10 pb-5 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <p className="text-[11px] uppercase tracking-[0.3em] text-slate-400">Strand Index</p>
                <h2
                  className="mt-2 text-2xl font-semibold text-white"
                  style={{ fontFamily: '"Iowan Old Style", "Palatino Linotype", "Book Antiqua", serif' }}
                >
                  Permanent Entry Registry
                </h2>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <label className="flex flex-col gap-2 text-xs uppercase tracking-[0.24em] text-slate-400">
                  Search
                  <input
                    className="rounded-full border border-white/10 bg-slate-950/70 px-4 py-2 text-sm normal-case tracking-normal text-white outline-none ring-0 transition placeholder:text-slate-500 focus:border-amber-200/35"
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder="Display name, token, profile"
                    value={query}
                  />
                </label>
                <label className="flex flex-col gap-2 text-xs uppercase tracking-[0.24em] text-slate-400">
                  State
                  <select
                    className="rounded-full border border-white/10 bg-slate-950/70 px-4 py-2 text-sm normal-case tracking-normal text-white outline-none"
                    onChange={(event) => setStatusFilter(event.target.value as EntryStatusFilter)}
                    value={statusFilter}
                  >
                    {STATUS_FILTERS.map((filter) => (
                      <option key={filter.value} value={filter.value}>
                        {filter.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </div>

            <div className="mt-5 space-y-4">
              {entriesError ? (
                <div className="rounded-[24px] border border-rose-400/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-100">
                  {entriesError}
                </div>
              ) : null}

              {isLoadingEntries ? (
                <div className="rounded-[24px] border border-white/10 bg-slate-950/50 px-4 py-10 text-center text-sm text-slate-400">
                  Loading permanent entry registry...
                </div>
              ) : null}

              {!isLoadingEntries && entriesResponse?.items.length === 0 ? (
                <div className="rounded-[24px] border border-dashed border-white/15 bg-slate-950/40 px-6 py-12 text-center">
                  <p
                    className="text-2xl text-white"
                    style={{ fontFamily: '"Iowan Old Style", "Palatino Linotype", "Book Antiqua", serif' }}
                  >
                    Quiet deck, no active strands yet
                  </p>
                  <p className="mx-auto mt-3 max-w-lg text-sm leading-6 text-slate-400">
                    Create the first permanent entry on the right. Once issued, it will appear here
                    with bridge activity and reconnect traces.
                  </p>
                </div>
              ) : null}

              {!isLoadingEntries &&
                entriesResponse?.items.map((entry) => {
                  const isSelected = entry.id === selectedEntryId;
                  return (
                    <div
                      className={surfaceCardClasses(isSelected)}
                      key={entry.id}
                      onClick={() => {
                        setSelectedEntryId(entry.id);
                        setActionMessage(null);
                      }}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          setSelectedEntryId(entry.id);
                          setActionMessage(null);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                    >
                      <div className="absolute right-0 top-0 h-24 w-24 rounded-full bg-amber-300/10 blur-3xl" />
                      <div className="relative flex flex-col gap-4 text-left">
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div>
                            <p className="text-[11px] uppercase tracking-[0.28em] text-slate-400">
                              {entry.profile}
                            </p>
                            <h3 className="mt-2 text-xl font-semibold text-white">{entry.displayName}</h3>
                            <p className="mt-1 text-sm text-slate-400">{entry.avatarKey}</p>
                          </div>
                          <span
                            className={`rounded-full border px-3 py-1 text-[11px] uppercase tracking-[0.22em] ${statusBadgeClasses(entry.effectiveStatus)}`}
                          >
                            {entry.effectiveStatus}
                          </span>
                        </div>

                        <div className="grid gap-3 sm:grid-cols-3">
                          <div>
                            <p className="text-[11px] uppercase tracking-[0.24em] text-slate-500">Agents</p>
                            <p className="mt-2 text-sm text-slate-200">
                              {entry.agentIds.join(', ')}
                            </p>
                          </div>
                          <div>
                            <p className="text-[11px] uppercase tracking-[0.24em] text-slate-500">Last Confirmed</p>
                            <p className="mt-2 text-sm text-slate-200">
                              {formatTimestamp(entry.lastConfirmedAt)}
                            </p>
                          </div>
                          <div>
                            <p className="text-[11px] uppercase tracking-[0.24em] text-slate-500">Issue Mix</p>
                            <p className="mt-2 text-sm text-slate-200">
                              {entry.issueCounts.confirmed} confirmed · {entry.issueCounts.pending} pending
                            </p>
                          </div>
                        </div>

                        <div className="flex flex-wrap items-center gap-3">
                          <button
                            className="rounded-full border border-white/10 bg-white/5 px-3 py-2 text-xs text-slate-200 transition hover:border-amber-200/30 hover:bg-white/10"
                            onClick={(event) => {
                              event.stopPropagation();
                              void handleCopyPublicUrl(entry);
                            }}
                            type="button"
                          >
                            {copiedUrlEntryId === entry.id ? 'Copied URL' : 'Copy URL'}
                          </button>
                          <p className="truncate text-xs text-slate-500">{entry.publicUrl}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          </section>

          <section className="grid gap-6">
            <article className="overflow-hidden rounded-[32px] border border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.05),rgba(15,23,42,0.94))] p-5 shadow-[0_18px_70px_rgba(2,6,23,0.4)]">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-[11px] uppercase tracking-[0.3em] text-slate-400">Forge</p>
                  <h2
                    className="mt-2 text-2xl font-semibold text-white"
                    style={{ fontFamily: '"Iowan Old Style", "Palatino Linotype", "Book Antiqua", serif' }}
                  >
                    Create Permanent Entry
                  </h2>
                </div>
                <div className="rounded-full border border-amber-300/25 bg-amber-300/10 px-3 py-1 text-[11px] uppercase tracking-[0.24em] text-amber-100">
                  New issuance path
                </div>
              </div>

              <form className="mt-5 grid gap-4" onSubmit={handleCreateEntry}>
                <label className="grid gap-2 text-sm text-slate-300">
                  <span>Avatar Key</span>
                  <input
                    className="rounded-[18px] border border-white/10 bg-slate-950/60 px-4 py-3 text-white outline-none transition placeholder:text-slate-500 focus:border-amber-200/35"
                    onChange={(event) =>
                      setCreateForm((current) => ({ ...current, avatarKey: event.target.value }))
                    }
                    value={createForm.avatarKey}
                  />
                </label>

                <label className="grid gap-2 text-sm text-slate-300">
                  <span>Display Name</span>
                  <input
                    className="rounded-[18px] border border-white/10 bg-slate-950/60 px-4 py-3 text-white outline-none transition placeholder:text-slate-500 focus:border-amber-200/35"
                    onChange={(event) =>
                      setCreateForm((current) => ({ ...current, displayName: event.target.value }))
                    }
                    value={createForm.displayName}
                  />
                </label>

                <label className="grid gap-2 text-sm text-slate-300">
                  <span>Profile</span>
                  <input
                    className="rounded-[18px] border border-white/10 bg-slate-950/60 px-4 py-3 text-white outline-none transition placeholder:text-slate-500 focus:border-amber-200/35"
                    onChange={(event) =>
                      setCreateForm((current) => ({ ...current, profile: event.target.value }))
                    }
                    value={createForm.profile}
                  />
                </label>

                <label className="grid gap-2 text-sm text-slate-300">
                  <span>Agent IDs</span>
                  <textarea
                    className="min-h-[88px] rounded-[18px] border border-white/10 bg-slate-950/60 px-4 py-3 text-white outline-none transition placeholder:text-slate-500 focus:border-amber-200/35"
                    onChange={(event) =>
                      setCreateForm((current) => ({ ...current, agentIds: event.target.value }))
                    }
                    placeholder="agri-a, agri-b"
                    value={createForm.agentIds}
                  />
                </label>

                <div className="grid gap-4 sm:grid-cols-2">
                  <label className="grid gap-2 text-sm text-slate-300">
                    <span>Default Agent ID</span>
                    <input
                      className="rounded-[18px] border border-white/10 bg-slate-950/60 px-4 py-3 text-white outline-none transition placeholder:text-slate-500 focus:border-amber-200/35"
                      onChange={(event) =>
                        setCreateForm((current) => ({ ...current, defaultAgentId: event.target.value }))
                      }
                      value={createForm.defaultAgentId}
                    />
                  </label>

                  <label className="grid gap-2 text-sm text-slate-300">
                    <span>Expires At</span>
                    <input
                      className="rounded-[18px] border border-white/10 bg-slate-950/60 px-4 py-3 text-white outline-none transition placeholder:text-slate-500 focus:border-amber-200/35"
                      onChange={(event) =>
                        setCreateForm((current) => ({ ...current, expiresAt: event.target.value }))
                      }
                      type="datetime-local"
                      value={createForm.expiresAt}
                    />
                  </label>
                </div>

                <button
                  className="rounded-full bg-[linear-gradient(90deg,#f5d06f,#f3b45a)] px-5 py-3 text-sm font-semibold text-slate-950 transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-60"
                  disabled={isCreating}
                  type="submit"
                >
                  {isCreating ? 'Creating Entry...' : 'Create Entry'}
                </button>
              </form>
            </article>

            <article className="overflow-hidden rounded-[32px] border border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.05),rgba(15,23,42,0.94))] p-5 shadow-[0_18px_70px_rgba(2,6,23,0.4)]">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-[11px] uppercase tracking-[0.3em] text-slate-400">Inspect</p>
                  <h2
                    className="mt-2 text-2xl font-semibold text-white"
                    style={{ fontFamily: '"Iowan Old Style", "Palatino Linotype", "Book Antiqua", serif' }}
                  >
                    Selected Entry
                  </h2>
                </div>
                {selectedEntry ? (
                  <span
                    className={`rounded-full border px-3 py-1 text-[11px] uppercase tracking-[0.22em] ${statusBadgeClasses(selectedEntry.effectiveStatus)}`}
                  >
                    {selectedEntry.effectiveStatus}
                  </span>
                ) : null}
              </div>

              {detailError ? (
                <div className="mt-5 rounded-[22px] border border-rose-400/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-100">
                  {detailError}
                </div>
              ) : null}

              {isLoadingDetail ? (
                <div className="mt-5 rounded-[22px] border border-white/10 bg-slate-950/50 px-4 py-10 text-center text-sm text-slate-400">
                  Loading selected entry...
                </div>
              ) : null}

              {!isLoadingDetail && !selectedEntry ? (
                <div className="mt-5 rounded-[22px] border border-dashed border-white/15 bg-slate-950/45 px-4 py-10 text-center text-sm text-slate-400">
                  Select an entry from the registry to inspect bridge activity, reconnect trails,
                  and control its public URL.
                </div>
              ) : null}

              {selectedEntry ? (
                <div className="mt-5 space-y-5">
                  <div className="rounded-[24px] border border-white/10 bg-slate-950/55 p-4">
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div>
                        <h3 className="text-xl font-semibold text-white">{selectedEntry.displayName}</h3>
                        <p className="mt-1 text-sm text-slate-400">{selectedEntry.avatarKey}</p>
                        <p className="mt-3 text-xs text-slate-500">{selectedEntry.publicUrl}</p>
                      </div>
                      <button
                        className="rounded-full border border-white/10 bg-white/5 px-3 py-2 text-xs text-slate-200 transition hover:border-amber-200/30 hover:bg-white/10"
                        onClick={() => {
                          void handleCopyPublicUrl(selectedEntry);
                        }}
                        type="button"
                      >
                        {copiedUrlEntryId === selectedEntry.id ? 'Copied URL' : 'Copy URL'}
                      </button>
                    </div>

                    <div className="mt-4 grid gap-3 sm:grid-cols-3">
                      <div>
                        <p className="text-[11px] uppercase tracking-[0.24em] text-slate-500">Last Issue</p>
                        <p className="mt-2 text-sm text-slate-200">{formatTimestamp(selectedEntry.lastIssueAt)}</p>
                      </div>
                      <div>
                        <p className="text-[11px] uppercase tracking-[0.24em] text-slate-500">Last Confirmed</p>
                        <p className="mt-2 text-sm text-slate-200">
                          {formatTimestamp(selectedEntry.lastConfirmedAt)}
                        </p>
                      </div>
                      <div>
                        <p className="text-[11px] uppercase tracking-[0.24em] text-slate-500">Current Links</p>
                        <p className="mt-2 text-sm text-slate-200">
                          {selectedEntry.currentLinkCount} current · {selectedEntry.totalLinkCount} total
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="grid gap-3 sm:grid-cols-4">
                    <div className="rounded-[20px] border border-white/10 bg-white/5 p-4">
                      <p className="text-[11px] uppercase tracking-[0.24em] text-slate-500">Confirmed</p>
                      <p className="mt-2 text-2xl font-semibold text-white">
                        {selectedEntry.issueCounts.confirmed}
                      </p>
                    </div>
                    <div className="rounded-[20px] border border-white/10 bg-white/5 p-4">
                      <p className="text-[11px] uppercase tracking-[0.24em] text-slate-500">Pending</p>
                      <p className="mt-2 text-2xl font-semibold text-white">
                        {selectedEntry.issueCounts.pending}
                      </p>
                    </div>
                    <div className="rounded-[20px] border border-white/10 bg-white/5 p-4">
                      <p className="text-[11px] uppercase tracking-[0.24em] text-slate-500">Expired</p>
                      <p className="mt-2 text-2xl font-semibold text-white">
                        {selectedEntry.issueCounts.expired}
                      </p>
                    </div>
                    <div className="rounded-[20px] border border-white/10 bg-white/5 p-4">
                      <p className="text-[11px] uppercase tracking-[0.24em] text-slate-500">Default Agent</p>
                      <p className="mt-2 text-sm font-semibold text-white">{selectedEntry.defaultAgentId}</p>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-3">
                    {!disableArmed ? (
                      <button
                        className="rounded-full border border-rose-300/30 bg-rose-500/10 px-4 py-2 text-sm text-rose-100 transition hover:bg-rose-500/15 disabled:cursor-not-allowed disabled:opacity-60"
                        disabled={isDisabling || selectedEntry.effectiveStatus === 'disabled'}
                        onClick={() => setDisableArmed(true)}
                        type="button"
                      >
                        Disable Entry
                      </button>
                    ) : (
                      <>
                        <button
                          className="rounded-full bg-rose-400 px-4 py-2 text-sm font-semibold text-slate-950 transition hover:bg-rose-300 disabled:cursor-not-allowed disabled:opacity-60"
                          disabled={isDisabling}
                          onClick={handleDisableEntry}
                          type="button"
                        >
                          {isDisabling ? 'Disabling...' : 'Confirm Disable'}
                        </button>
                        <button
                          className="rounded-full border border-white/10 px-4 py-2 text-sm text-slate-200 transition hover:bg-white/5"
                          disabled={isDisabling}
                          onClick={() => setDisableArmed(false)}
                          type="button"
                        >
                          Cancel
                        </button>
                      </>
                    )}
                    <span className="text-xs uppercase tracking-[0.24em] text-slate-500">
                      Issuable: {selectedEntry.canIssue ? 'yes' : 'no'}
                    </span>
                  </div>
                </div>
              ) : null}
            </article>

            <article className="overflow-hidden rounded-[32px] border border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.05),rgba(15,23,42,0.94))] p-5 shadow-[0_18px_70px_rgba(2,6,23,0.4)]">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-[11px] uppercase tracking-[0.3em] text-slate-400">Signal Trail</p>
                  <h2
                    className="mt-2 text-2xl font-semibold text-white"
                    style={{ fontFamily: '"Iowan Old Style", "Palatino Linotype", "Book Antiqua", serif' }}
                  >
                    Recent Activity
                  </h2>
                </div>
                <span className="text-xs uppercase tracking-[0.24em] text-slate-500">
                  Issues & reconnects
                </span>
              </div>

              <div className="mt-5 grid gap-4 xl:grid-cols-2">
                <div className="space-y-3">
                  <p className="text-sm font-medium text-slate-200">Latest issues</p>
                  {activity?.issues.length ? (
                    activity.issues.map((issue) => (
                      <div
                        className="rounded-[22px] border border-white/10 bg-slate-950/55 p-4"
                        key={issue.issueId}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <p className="text-sm font-semibold text-white">{issue.viewerToken}</p>
                            <p className="mt-1 text-xs text-slate-500">{issue.issueId}</p>
                          </div>
                          <span
                            className={`rounded-full border px-3 py-1 text-[11px] uppercase tracking-[0.22em] ${statusBadgeClasses(issue.status)}`}
                          >
                            {issue.status}
                          </span>
                        </div>
                        <div className="mt-3 space-y-1 text-sm text-slate-300">
                          <p>Issued {formatTimestamp(issue.createdAt)}</p>
                          <p>Expires {formatTimestamp(issue.expiresAt)}</p>
                          {issue.bindingSenderId ? <p>{issue.bindingSenderId}</p> : null}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="rounded-[22px] border border-dashed border-white/15 bg-slate-950/45 px-4 py-8 text-sm text-slate-400">
                      No issue activity yet.
                    </div>
                  )}
                </div>

                <div className="space-y-3">
                  <p className="text-sm font-medium text-slate-200">Recent links</p>
                  {activity?.recentLinks.length ? (
                    activity.recentLinks.map((link) => (
                      <div
                        className="rounded-[22px] border border-white/10 bg-slate-950/55 p-4"
                        key={link.id}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <p className="text-sm font-semibold text-white">{link.userId}</p>
                            <p className="mt-1 text-xs text-slate-500">Link {link.id}</p>
                          </div>
                          <span
                            className={`rounded-full border px-3 py-1 text-[11px] uppercase tracking-[0.22em] ${statusBadgeClasses(link.isCurrent ? 'active' : 'disabled')}`}
                          >
                            {link.isCurrent ? 'current' : 'historical'}
                          </span>
                        </div>
                        <div className="mt-3 space-y-1 text-sm text-slate-300">
                          <p>First seen {formatTimestamp(link.firstConnectedAt)}</p>
                          <p>Last connected {formatTimestamp(link.lastConnectedAt)}</p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="rounded-[22px] border border-dashed border-white/15 bg-slate-950/45 px-4 py-8 text-sm text-slate-400">
                      No reconnect trail yet.
                    </div>
                  )}
                </div>
              </div>
            </article>
          </section>
        </div>
        ) : null}
      </div>
    </div>
  );
}
